/**
 * Anagrafica fornitori.
 *
 * Tre cose in una pagina, nell'ordine in cui servono: l'elenco con la scheda
 * di ciascuno, i nomi ancora da abbinare (che e' un lavoro che finisce, non
 * una funzione permanente), e il confronto fra quanto e' uscito dalla banca e
 * quanto sarebbe dovuto costare quello che abbiamo consumato.
 *
 * Il confronto e' su un anno di default, non su un mese: si compra a scatti e
 * si consuma in continuo, quindi su un mese solo lo scostamento e' magazzino,
 * non food cost.
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import {
  Truck, Plus, Pencil, Link2, Scale, ChevronRight, Ban
} from 'lucide-react';
import apiService, {
  CeFornitore, SchedaFornitore, NomeDaAbbinare, ConfrontoFornitore
} from '../services/api';
import {
  PageContainer, PageHeader, Button, Card, CardBody, Alert, Badge,
  LoadingState, Modal, FormGroup, FormRow, FormActions, Label, Input,
  Select, Textarea, useToast, useConfirm
} from '../components/ui';

const Schede = styled.div`
  display: flex;
  gap: var(--space-sm);
  margin-bottom: var(--space-xl);
  flex-wrap: wrap;
`;

const Tab = styled.button<{ $attiva: boolean }>`
  padding: var(--space-sm) var(--space-lg);
  border-radius: var(--radius-md);
  border: 1px solid ${(p) => (p.$attiva ? 'var(--primary)' : 'var(--border-color)')};
  background: ${(p) => (p.$attiva ? 'var(--primary)' : 'transparent')};
  color: ${(p) => (p.$attiva ? '#fff' : 'var(--text-primary)')};
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: var(--space-xs);
`;

const Filtri = styled.div`
  display: flex;
  gap: var(--space-md);
  align-items: center;
  margin-bottom: var(--space-lg);
  flex-wrap: wrap;

  input, select { max-width: 18rem; }
`;

const Griglia = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: var(--space-lg);

  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const RigaTitolo = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-sm);

  h3 {
    margin: 0 0 var(--space-xs);
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-semibold);
  }
`;

const Dettagli = styled.dl`
  margin: var(--space-md) 0 0;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: var(--space-xs) var(--space-md);
  font-size: var(--font-size-sm);

  dt { color: var(--text-secondary); }
  dd { margin: 0; }
`;

const Tabella = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-sm);

  th, td {
    padding: var(--space-sm);
    text-align: left;
    border-bottom: 1px solid var(--border-color);
  }
  th { color: var(--text-secondary); font-weight: var(--font-weight-medium); }
  td.numero, th.numero { text-align: right; font-variant-numeric: tabular-nums; }
`;

const Scostamento = styled.span<{ $sopra: boolean }>`
  color: ${(p) => (p.$sopra ? 'var(--danger, #c0392b)' : 'var(--success, #27ae60)')};
  font-variant-numeric: tabular-nums;
`;

const Nota = styled.p`
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  margin: 0 0 var(--space-lg);
  max-width: 46rem;
  line-height: 1.5;
`;

const euro = (valore: number | string | null | undefined) =>
  `€ ${Number(valore || 0).toLocaleString('it-IT', {
    minimumFractionDigits: 2, maximumFractionDigits: 2
  })}`;

const VUOTO: Partial<CeFornitore> = {
  nome: '', partita_iva: '', referente: '', telefono: '', email: '',
  indirizzo: '', citta: '', condizioni_pagamento: '', giorni_consegna: '',
  fornisce_ingredienti: true, note: ''
};

type Vista = 'elenco' | 'abbinamenti' | 'confronto';

const FornitoriPage: React.FC = () => {
  const toast = useToast();
  const conferma = useConfirm();

  const [vista, setVista] = useState<Vista>('elenco');
  const [caricamento, setCaricamento] = useState(true);
  const [fornitori, setFornitori] = useState<CeFornitore[]>([]);
  const [ricerca, setRicerca] = useState('');
  const [soloIngredienti, setSoloIngredienti] = useState(false);

  const [scheda, setScheda] = useState<SchedaFornitore | null>(null);
  const [modifica, setModifica] = useState<Partial<CeFornitore> | null>(null);

  const [daAbbinare, setDaAbbinare] = useState<NomeDaAbbinare[]>([]);
  const [scelte, setScelte] = useState<Record<string, number | ''>>({});

  const [confronto, setConfronto] = useState<ConfrontoFornitore[]>([]);
  const [periodo, setPeriodo] = useState(() => {
    const oggi = new Date();
    const unAnnoFa = new Date(oggi.getTime() - 365 * 86400000);
    return { da: unAnnoFa.toISOString().slice(0, 10), a: oggi.toISOString().slice(0, 10) };
  });

  const caricaFornitori = useCallback(async () => {
    try {
      setFornitori(await apiService.getFornitori({ soloIngredienti }));
    } catch {
      toast.error('Non riesco a leggere i fornitori');
    } finally {
      setCaricamento(false);
    }
  }, [soloIngredienti, toast]);

  useEffect(() => { caricaFornitori(); }, [caricaFornitori]);

  useEffect(() => {
    if (vista !== 'abbinamenti') return;
    apiService.getNomiDaAbbinare()
      .then(setDaAbbinare)
      .catch(() => toast.error('Non riesco a leggere i nomi da abbinare'));
  }, [vista, toast]);

  useEffect(() => {
    if (vista !== 'confronto') return;
    apiService.getConfrontoFornitori(periodo.da, periodo.a)
      .then((esito) => setConfronto(esito.righe))
      .catch(() => toast.error('Non riesco a calcolare il confronto'));
  }, [vista, periodo, toast]);

  const apriScheda = async (id: number) => {
    try {
      setScheda(await apiService.getSchedaFornitore(id));
    } catch {
      toast.error('Non riesco ad aprire la scheda');
    }
  };

  const salva = async () => {
    if (!modifica?.nome?.trim()) {
      toast.error('Il nome e\' obbligatorio');
      return;
    }
    try {
      if (modifica.id) {
        await apiService.aggiornaFornitore(modifica.id, modifica);
        toast.success('Fornitore aggiornato');
      } else {
        await apiService.creaFornitore(modifica);
        toast.success('Fornitore creato');
      }
      setModifica(null);
      caricaFornitori();
    } catch (errore: any) {
      toast.error(errore?.response?.data?.error || 'Non riesco a salvare');
    }
  };

  const disattiva = async (fornitore: CeFornitore) => {
    const ok = await conferma({
      title: `Disattivare ${fornitore.nome}?`,
      message: 'Il fornitore resta attaccato ai movimenti degli anni passati, '
        + 'che continuano a tornare: sparisce solo dalle tendine.'
    });
    if (!ok) return;
    await apiService.disattivaFornitore(fornitore.id);
    caricaFornitori();
  };

  const abbina = async (riga: NomeDaAbbinare) => {
    const fornitoreId = scelte[riga.testo];
    if (!fornitoreId) return;
    try {
      const esito = await apiService.abbinaFornitore(riga.testo, Number(fornitoreId));
      toast.success(esito.messaggio);
      setDaAbbinare(await apiService.getNomiDaAbbinare());
      caricaFornitori();
    } catch (errore: any) {
      toast.error(errore?.response?.data?.error || 'Non riesco ad abbinare');
    }
  };

  const visibili = fornitori.filter((f) => {
    const testo = ricerca.trim().toLowerCase();
    if (!testo) return true;
    return [f.nome, f.citta, f.referente, f.partita_iva]
      .some((campo) => (campo || '').toLowerCase().includes(testo));
  });

  if (caricamento) return <LoadingState />;

  return (
    <PageContainer>
      <PageHeader
        title="Fornitori"
        subtitle="Chi ci fornisce, cosa ci costa, e quanto di quello che compriamo finisce davvero nelle vasche"
        icon={<Truck />}
        actions={
          <Button onClick={() => setModifica({ ...VUOTO })}>
            <Plus size={16} /> Nuovo fornitore
          </Button>
        }
      />

      <Schede>
        <Tab $attiva={vista === 'elenco'} onClick={() => setVista('elenco')}>
          <Truck size={16} /> Elenco
        </Tab>
        <Tab $attiva={vista === 'abbinamenti'} onClick={() => setVista('abbinamenti')}>
          <Link2 size={16} /> Da abbinare
          {daAbbinare.length > 0 && <Badge $variant="warning">{daAbbinare.length}</Badge>}
        </Tab>
        <Tab $attiva={vista === 'confronto'} onClick={() => setVista('confronto')}>
          <Scale size={16} /> Teorico contro reale
        </Tab>
      </Schede>

      {vista === 'elenco' && (
        <>
          <Filtri>
            <Input
              placeholder="Cerca per nome, citta', referente, partita IVA"
              value={ricerca}
              onChange={(e) => setRicerca(e.target.value)}
            />
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={soloIngredienti}
                onChange={(e) => setSoloIngredienti(e.target.checked)}
              />
              Solo chi fornisce ingredienti
            </label>
          </Filtri>

          <Griglia>
            {visibili.map((fornitore) => (
              <Card key={fornitore.id}>
                <CardBody>
                  <RigaTitolo>
                    <div>
                      <h3>{fornitore.nome}</h3>
                      {fornitore.categoria_nome && (
                        <Badge $variant="neutral">{fornitore.categoria_nome}</Badge>
                      )}
                      {!fornitore.attivo && <Badge $variant="error">Disattivato</Badge>}
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <Button $size="sm" $variant="ghost" onClick={() => setModifica(fornitore)}>
                        <Pencil size={14} />
                      </Button>
                      {fornitore.attivo !== false && (
                        <Button $size="sm" $variant="ghost" onClick={() => disattiva(fornitore)}>
                          <Ban size={14} />
                        </Button>
                      )}
                    </div>
                  </RigaTitolo>

                  <Dettagli>
                    {fornitore.referente && (<><dt>Referente</dt><dd>{fornitore.referente}</dd></>)}
                    {fornitore.telefono && (<><dt>Telefono</dt><dd>{fornitore.telefono}</dd></>)}
                    {fornitore.condizioni_pagamento && (
                      <><dt>Pagamento</dt><dd>{fornitore.condizioni_pagamento}</dd></>
                    )}
                    <dt>Ingredienti</dt>
                    <dd>{fornitore.ingredienti_forniti || 0}</dd>
                    <dt>Ultimi 12 mesi</dt>
                    <dd>{euro(fornitore.speso_ultimi_12_mesi)}</dd>
                  </Dettagli>

                  <Button
                    $size="sm"
                    $variant="ghost"
                    style={{ marginTop: 'var(--space-md)' }}
                    onClick={() => apriScheda(fornitore.id)}
                  >
                    Apri la scheda <ChevronRight size={14} />
                  </Button>
                </CardBody>
              </Card>
            ))}
          </Griglia>
        </>
      )}

      {vista === 'abbinamenti' && (
        <>
          <Nota>
            Questi nomi erano scritti a mano dentro gli ingredienti, prima che
            l'anagrafica esistesse. Quelli identici a un fornitore sono gia' stati
            abbinati da soli; qui restano quelli su cui una macchina non puo'
            decidere senza rischiare di attribuire una spesa alla persona
            sbagliata. Quello che scegli vale anche per quello che arrivera' dopo,
            righe di fattura comprese.
          </Nota>

          {daAbbinare.length === 0 ? (
            <Alert $variant="success">Non c'e' piu' niente da abbinare.</Alert>
          ) : (
            <Card><CardBody>
              <Tabella>
                <thead>
                  <tr>
                    <th>Nome scritto a mano</th>
                    <th className="numero">Ingredienti</th>
                    <th>Fornitore</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {daAbbinare.map((riga) => (
                    <tr key={riga.testo}>
                      <td>
                        <strong>{riga.testo}</strong>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                          {riga.nomi_ingredienti.slice(0, 4).join(', ')}
                          {riga.nomi_ingredienti.length > 4 && ` e altri ${riga.nomi_ingredienti.length - 4}`}
                        </div>
                      </td>
                      <td className="numero">{riga.ingredienti}</td>
                      <td>
                        <Select
                          value={scelte[riga.testo] ?? ''}
                          onChange={(e) => setScelte({
                            ...scelte,
                            [riga.testo]: e.target.value ? Number(e.target.value) : ''
                          })}
                        >
                          <option value="">Scegli…</option>
                          {riga.proposte.length > 0 && (
                            <optgroup label="Somigliano a questo nome">
                              {riga.proposte.map((p) => (
                                <option key={p.id} value={p.id}>{p.nome}</option>
                              ))}
                            </optgroup>
                          )}
                          <optgroup label="Tutti i fornitori">
                            {fornitori.map((f) => (
                              <option key={f.id} value={f.id}>{f.nome}</option>
                            ))}
                          </optgroup>
                        </Select>
                      </td>
                      <td>
                        <Button
                          $size="sm"
                          disabled={!scelte[riga.testo]}
                          onClick={() => abbina(riga)}
                        >
                          Abbina
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Tabella>
            </CardBody></Card>
          )}
        </>
      )}

      {vista === 'confronto' && (
        <>
          <Nota>
            A sinistra quanto sarebbe dovuto costare, secondo le ricette, tutto
            quello che risulta prodotto nel periodo. A destra quanto e' uscito
            davvero dal conto per quel fornitore. Le due cose non coincidono mai
            mese per mese, perche' si compra a scatti e si consuma in continuo:
            su un anno invece il magazzino si compensa e lo scostamento comincia
            a voler dire qualcosa.
          </Nota>

          <Filtri>
            <Label>
              Dal
              <Input
                type="date"
                value={periodo.da}
                onChange={(e) => setPeriodo({ ...periodo, da: e.target.value })}
              />
            </Label>
            <Label>
              al
              <Input
                type="date"
                value={periodo.a}
                onChange={(e) => setPeriodo({ ...periodo, a: e.target.value })}
              />
            </Label>
          </Filtri>

          <Card><CardBody>
            <Tabella>
              <thead>
                <tr>
                  <th>Fornitore</th>
                  <th className="numero">Teorico</th>
                  <th className="numero">Reale</th>
                  <th className="numero">Scostamento</th>
                </tr>
              </thead>
              <tbody>
                {confronto.map((riga) => (
                  <tr key={riga.fornitore_id ?? 'nessuno'}>
                    <td>
                      {riga.fornitore_nome}
                      {!riga.fornitore_id && (
                        <Badge $variant="warning" style={{ marginLeft: 8 }}>
                          da abbinare
                        </Badge>
                      )}
                    </td>
                    <td className="numero">{euro(riga.teorico)}</td>
                    <td className="numero">{euro(riga.reale)}</td>
                    <td className="numero">
                      <Scostamento $sopra={riga.scostamento > 0}>
                        {riga.scostamento > 0 ? '+' : ''}{euro(riga.scostamento)}
                      </Scostamento>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Tabella>
          </CardBody></Card>
        </>
      )}

      {/* Scheda del fornitore */}
      {scheda && (
        <Modal
          open
          onClose={() => setScheda(null)}
          title={scheda.fornitore.nome}
          maxWidth="52rem"
        >
          {scheda.confronto && (
            <Alert $variant={Math.abs(scheda.confronto.scostamento) > scheda.confronto.teorico * 0.3
              ? 'warning' : 'info'}>
              Da {scheda.periodo.da} a {scheda.periodo.a}: teorico {euro(scheda.confronto.teorico)},
              uscito dal conto {euro(scheda.confronto.reale)}, scostamento{' '}
              {scheda.confronto.scostamento > 0 ? '+' : ''}{euro(scheda.confronto.scostamento)}.
            </Alert>
          )}

          <Dettagli>
            {scheda.fornitore.partita_iva && (<><dt>Partita IVA</dt><dd>{scheda.fornitore.partita_iva}</dd></>)}
            {scheda.fornitore.referente && (<><dt>Referente</dt><dd>{scheda.fornitore.referente}</dd></>)}
            {scheda.fornitore.telefono && (<><dt>Telefono</dt><dd>{scheda.fornitore.telefono}</dd></>)}
            {scheda.fornitore.email && (<><dt>Email</dt><dd>{scheda.fornitore.email}</dd></>)}
            {scheda.fornitore.indirizzo && (
              <><dt>Indirizzo</dt>
                <dd>{scheda.fornitore.indirizzo} {scheda.fornitore.cap} {scheda.fornitore.citta}</dd></>
            )}
            {scheda.fornitore.condizioni_pagamento && (
              <><dt>Pagamento</dt><dd>{scheda.fornitore.condizioni_pagamento}</dd></>
            )}
            {scheda.fornitore.giorni_consegna && (
              <><dt>Consegne</dt><dd>{scheda.fornitore.giorni_consegna}</dd></>
            )}
          </Dettagli>

          {scheda.alias.length > 0 && (
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              Riconosciuto anche come: {scheda.alias.map((a) => a.alias).join(', ')}
            </p>
          )}

          <h4>Ingredienti ({scheda.ingredienti.length})</h4>
          <Tabella>
            <thead>
              <tr>
                <th>Ingrediente</th>
                <th className="numero">Costo</th>
                <th className="numero">Usato in</th>
              </tr>
            </thead>
            <tbody>
              {scheda.ingredienti.map((ingrediente) => (
                <tr key={ingrediente.id}>
                  <td>{ingrediente.nome}</td>
                  <td className="numero">
                    {euro(ingrediente.prezzo_fornitore ?? ingrediente.costo_per_unita)}
                    /{ingrediente.unita_misura}
                  </td>
                  <td className="numero">{ingrediente.usato_in_gusti} gusti</td>
                </tr>
              ))}
            </tbody>
          </Tabella>

          {scheda.consumoTeorico && scheda.consumoTeorico.length > 0 && (
            <>
              <h4>Consumato nel periodo, secondo le ricette</h4>
              <Tabella>
                <thead>
                  <tr>
                    <th>Ingrediente</th>
                    <th className="numero">Quantita'</th>
                    <th className="numero">Costo teorico</th>
                  </tr>
                </thead>
                <tbody>
                  {scheda.consumoTeorico.map((riga) => (
                    <tr key={riga.ingrediente_id}>
                      <td>{riga.ingrediente}</td>
                      <td className="numero">
                        {Number(riga.quantita).toLocaleString('it-IT', { maximumFractionDigits: 1 })}
                        {' '}{riga.unita_misura}
                      </td>
                      <td className="numero">{euro(riga.costo)}</td>
                    </tr>
                  ))}
                </tbody>
              </Tabella>
            </>
          )}
        </Modal>
      )}

      {/* Nuovo / modifica */}
      {modifica && (
        <Modal
          open
          onClose={() => setModifica(null)}
          title={modifica.id ? `Modifica ${modifica.nome}` : 'Nuovo fornitore'}
        >
          <FormGroup>
            <Label>Nome *</Label>
            <Input
              value={modifica.nome || ''}
              onChange={(e) => setModifica({ ...modifica, nome: e.target.value })}
              autoFocus
            />
          </FormGroup>

          <FormRow>
            <FormGroup>
              <Label>Partita IVA</Label>
              <Input
                value={modifica.partita_iva || ''}
                onChange={(e) => setModifica({ ...modifica, partita_iva: e.target.value })}
              />
            </FormGroup>
            <FormGroup>
              <Label>Referente</Label>
              <Input
                value={modifica.referente || ''}
                onChange={(e) => setModifica({ ...modifica, referente: e.target.value })}
              />
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <Label>Telefono</Label>
              <Input
                value={modifica.telefono || ''}
                onChange={(e) => setModifica({ ...modifica, telefono: e.target.value })}
              />
            </FormGroup>
            <FormGroup>
              <Label>Email</Label>
              <Input
                value={modifica.email || ''}
                onChange={(e) => setModifica({ ...modifica, email: e.target.value })}
              />
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <Label>Indirizzo</Label>
              <Input
                value={modifica.indirizzo || ''}
                onChange={(e) => setModifica({ ...modifica, indirizzo: e.target.value })}
              />
            </FormGroup>
            <FormGroup>
              <Label>Citta'</Label>
              <Input
                value={modifica.citta || ''}
                onChange={(e) => setModifica({ ...modifica, citta: e.target.value })}
              />
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <Label>Condizioni di pagamento</Label>
              <Input
                placeholder="es. 30 giorni fine mese"
                value={modifica.condizioni_pagamento || ''}
                onChange={(e) => setModifica({ ...modifica, condizioni_pagamento: e.target.value })}
              />
            </FormGroup>
            <FormGroup>
              <Label>Giorni di consegna</Label>
              <Input
                placeholder="es. martedi' e venerdi'"
                value={modifica.giorni_consegna || ''}
                onChange={(e) => setModifica({ ...modifica, giorni_consegna: e.target.value })}
              />
            </FormGroup>
          </FormRow>

          <FormGroup>
            <Label>
              <input
                type="checkbox"
                checked={modifica.fornisce_ingredienti !== false}
                onChange={(e) => setModifica({ ...modifica, fornisce_ingredienti: e.target.checked })}
              />
              {' '}Fornisce ingredienti
            </Label>
          </FormGroup>

          <FormGroup>
            <Label>Note</Label>
            <Textarea
              rows={3}
              value={modifica.note || ''}
              onChange={(e) => setModifica({ ...modifica, note: e.target.value })}
            />
          </FormGroup>

          <FormActions>
            <Button $variant="ghost" onClick={() => setModifica(null)}>Annulla</Button>
            <Button onClick={salva}>Salva</Button>
          </FormActions>
        </Modal>
      )}
    </PageContainer>
  );
};

export default FornitoriPage;
